import { Bell } from "lucide-react";
import { Button } from "@capacit-com/lara-law-shared-ui";
import { useEffect, useState, useRef, useCallback } from "react";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@capacit-com/lara-law-shared-ui";
import NotificationGroupList from "./NotificationGroupList";
import { Spinner } from "@capacit-com/lara-law-shared-ui";
import { motion, useAnimation } from "framer-motion";
import { cn } from "@/lib/utils";
import { useNotificationContext } from "@/providers/notification/NotificationHubContext";

const NotificationButton = () => {
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);

  const {
    notifications,
    dismissAllNotifications,
    dismissNotification,
    dismissNotifications,
    isDismissingAllNotifications,
    isDismissingNotification,
    loadMoreActiveNotifications,
    hasMoreActive,
    isLoadingMoreActive,
    activeCount,
    seeAllNotifications,
    isMarkingAsSeen,
  } = useNotificationContext();

  const [prevCount, setPrevCount] = useState(activeCount);
  const notificationNumberRef = useRef<HTMLDivElement>(null);
  const bellControls = useAnimation();

  useEffect(() => {
    if (activeCount > prevCount) {
      bellControls.start({
        rotate: [0, -15, 15, -10, 10, -5, 5, 0],
        transition: {
          duration: 0.6,
          ease: "easeInOut",
        },
      });
    }
    setPrevCount(activeCount);
  }, [activeCount, prevCount, bellControls]);

  // Infinite scroll observer for last notification
  const observer = useRef<IntersectionObserver | null>(null);
  const lastNotificationRef = useCallback(
    (node: HTMLDivElement | null) => {
      if (isLoadingMoreActive) return;
      if (observer.current) observer.current.disconnect();
      observer.current = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting && hasMoreActive) {
          loadMoreActiveNotifications();
        }
      });
      if (node) observer.current.observe(node);
    },
    [isLoadingMoreActive, hasMoreActive, loadMoreActiveNotifications]
  );

  return (
    <div className="flex items-center justify-center">
      <Popover open={isPopoverOpen} onOpenChange={setIsPopoverOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="ghost"
            size="icon"
            className="relative"
            onClick={
              isMarkingAsSeen || activeCount === 0
                ? undefined
                : seeAllNotifications
            }
          >
            <motion.div animate={bellControls}>
              <Bell className="size-4.5" />
            </motion.div>
            {/* Static notification dot */}
            {activeCount > 0 && (
              <div
                ref={notificationNumberRef}
                className={cn(
                  "absolute top-1 right-1 bg-warning text-black dark:text-white font-bold rounded-xs text-[8px] size-fit min-h-3 min-w-3.5 px-1 py-0.5 flex items-center justify-center opacity-0 transition-opacity duration-300",
                  activeCount > 0 && "opacity-100"
                )}
              >
                {activeCount > 9 ? "9+" : activeCount}
              </div>
            )}
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-80 p-0">
          <div className="flex flex-col">
            <div className="p-2 border-b flex justify-between items-center">
              <div className="flex gap-1">
                <h3 className="font-semibold text-sm">Nye notifikationer</h3>
              </div>

              <Button
                size="sm"
                variant="ghost"
                className="text-[10px] px-2 py-1 rounded-sm cursor-pointer h-6"
                disabled={isDismissingAllNotifications}
                onClick={dismissAllNotifications}
              >
                {isDismissingAllNotifications ? (
                  <Spinner className="size-4 w-24" />
                ) : (
                  "Ryd notifikationer"
                )}
              </Button>
            </div>
            <div className="max-h-96 overflow-y-auto">
              {notifications && notifications.length > 0 ? (
                <>
                <NotificationGroupList
                  notifications={notifications}
                  dismissNotification={dismissNotification}
                  dismissNotifications={dismissNotifications}
                  isDismissingNotification={isDismissingNotification}
                  onNavigate={() => setIsPopoverOpen(false)}
                />
                  <div ref={lastNotificationRef} className="h-2" />
                </>
              ) : (
                <div className="p-4 text-center text-muted-foreground">
                  Ingen notifikationer
                </div>
              )}
              {isLoadingMoreActive && (
                <div className="py-2 text-center text-sm text-muted-foreground">
                  <Spinner className="size-4" /> Indlæser flere...
                </div>
              )}
            </div>
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
};

export default NotificationButton;
